// src/utils/participantSelfAccess.js
//
// Pass 85.1A — CANONICAL STUDENT SELF-ACCESS. A signed-in coaching participant resolves their identity as:
//
//   Auth uid → participantProfiles where authUserId == uid → participant(s) → organization(s) → selected org.
//
// This REPLACES the legacy student tenancy assumption (Auth uid + `users.orgId`). Identity comes ONLY from an explicit
// participant profile whose CURRENT `authUserId` equals the caller — never from `users.orgId`, `clientProfiles.userId`,
// `lesson.studentId`, `video.userId`, or an email match. A single Auth account may legitimately hold participant P_A in
// Org A and P_B in Org B; they are distinct and never merged. Only safe student-facing profile fields are exposed here.

import { collection, query, where, getDocs } from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from './platformSchema';

const str = (v) => String(v == null ? '' : v).trim();

// Safe student-facing participant profile fields (NO commercial/CRM/private data — that lives in clientProfiles).
export const SAFE_STUDENT_PROFILE_FIELDS = Object.freeze(['displayName', 'preferredName', 'primaryDiscipline', 'goals']);

// Normalize one participantProfiles doc to the safe student shape (drops any commercial field defensively).
export function toStudentParticipant(docData = {}, id = '') {
  const participantId = str(docData.participantId) || str(id);
  const out = {
    participantId,
    organizationId: str(docData.organizationId),
    authUserId: str(docData.authUserId),
  };
  for (const k of SAFE_STUDENT_PROFILE_FIELDS) out[k] = str(docData[k]);
  return out;
}

// ── Pure model: given the caller's participant profiles + a (possibly stale) selected org, resolve the student context.
//   • orgOptions: distinct organizationIds the caller is a participant in (each with a representative displayName).
//   • selectedOrganizationId: the requested org if it is a real option, else the sole option, else '' (forces a choice).
//   • activeParticipant: the participant for the selected org, or null.
//   • ambiguous: true when the selected org has MORE THAN ONE participant for this Auth account — we DO NOT silently
//     pick one; the caller surfaces an explicit "which profile?" state instead.
export function buildStudentContextModel(profiles = [], requestedOrganizationId = '') {
  const clean = (Array.isArray(profiles) ? profiles : [])
    .map((p) => (p && p.participantId ? p : toStudentParticipant(p)))
    .filter((p) => str(p.participantId) && str(p.organizationId));

  const byOrg = {};
  for (const p of clean) {
    const org = str(p.organizationId);
    (byOrg[org] = byOrg[org] || []).push(p);
  }
  const orgOptions = Object.keys(byOrg).sort().map((org) => ({
    organizationId: org,
    participantCount: byOrg[org].length,
    displayName: str(byOrg[org][0].displayName) || str(byOrg[org][0].preferredName),
  }));

  const requested = str(requestedOrganizationId);
  let selectedOrganizationId = '';
  if (requested && byOrg[requested]) selectedOrganizationId = requested;
  else if (orgOptions.length === 1) selectedOrganizationId = orgOptions[0].organizationId;

  const inOrg = selectedOrganizationId ? byOrg[selectedOrganizationId] : [];
  const ambiguous = inOrg.length > 1;
  const activeParticipant = !ambiguous && inOrg.length === 1 ? inOrg[0] : null;

  return {
    orgOptions,
    hasAnyParticipant: clean.length > 0,
    needsOrgChoice: orgOptions.length > 1 && !selectedOrganizationId,
    selectedOrganizationId,
    activeParticipant,
    ambiguous,
    ambiguousParticipants: ambiguous ? inOrg.slice() : [],
  };
}

// ── Pass 85.1A.2 — the CANONICAL STUDENT ACCESS LANE. A signed-in student's data access is chosen from ONE explicit
// state machine, so a two-boolean `modern`/`!modern` classifier can NEVER conflate an UNRESOLVED canonical account with a
// true LEGACY account. **LEGACY is permitted ONLY after a SUCCESSFUL participant lookup proves ZERO profiles.** A read
// failure, a still-loading context, an unresolved multi-org choice, and an ambiguous same-org link all FAIL CLOSED away
// from legacy access — they render their own state and perform NO legacy reads/writes and NO modern query.
//
//   'loading'   — the participant context is still resolving (NOT legacy).
//   'error'     — the participant lookup FAILED (NOT legacy — error ≠ "no participant exists").
//   'legacy'    — the lookup SUCCEEDED and returned ZERO participant profiles (the ONLY legacy state).
//   'needs_org' — a canonical account with participants in >1 org and no selected org (must choose first).
//   'ambiguous' — a canonical account with >1 participant in the selected org (never guessed).
//   'modern'    — a fully resolved canonical account: an active participant + a selected org.
export const STUDENT_ACCESS_LANES = Object.freeze(['loading', 'error', 'legacy', 'needs_org', 'ambiguous', 'modern']);
export function resolveStudentAccessLane(ctx = {}) {
  if (ctx.loading) return 'loading';
  if (ctx.error) return 'error';
  if (!ctx.hasAnyParticipant) return 'legacy';        // SUCCESSFUL lookup, zero profiles → the ONLY legacy state
  if (ctx.ambiguous) return 'ambiguous';
  if (ctx.needsOrgChoice) return 'needs_org';
  if (str(ctx.activeParticipantId) && str(ctx.selectedOrganizationId)) return 'modern';
  return 'needs_org';                                  // canonical but org/participant not resolved → fail closed (never legacy)
}

// ── Thin async loader: participantProfiles where authUserId == uid (single-field query; each returned doc satisfies the
// participant self-read rule). Returns the safe student participant shapes. Throws on a real read failure (error ≠ empty).
export async function loadParticipantSelfProfiles(db, authUid) {
  const uid = str(authUid);
  if (!uid) return [];
  const snap = await getDocs(query(
    collection(db, PLATFORM_COLLECTIONS.PARTICIPANT_PROFILES),
    where('authUserId', '==', uid),
  ));
  return snap.docs.map((d) => toStudentParticipant(d.data(), d.id));
}
